import { useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from "../i18n";

/**
 * Hook que sincroniza el idioma de i18n con la URL
 * - Cuando cambia el parámetro :lang en la URL, actualiza i18n
 * - Cuando cambia el idioma en i18n (por ejemplo desde un selector), actualiza la URL
 */
export const useLanguageSync = () => {
  const { i18n } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Sincronizar URL -> i18n
  useEffect(() => {
    if (lang && SUPPORTED_LANGUAGES.includes(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  // Sincronizar i18n -> URL (cuando el usuario cambia el idioma desde la UI)
  useEffect(() => {
    // Solo actualizar si estamos en una ruta con prefijo de idioma
    const pathParts = location.pathname.split("/").filter(Boolean);
    const currentLangInUrl = pathParts[0];

    // Si el idioma actual de i18n es diferente al de la URL
    if (
      SUPPORTED_LANGUAGES.includes(currentLangInUrl) &&
      i18n.language !== currentLangInUrl
    ) {
      // Reemplazar el idioma en la URL
      const newPath = location.pathname.replace(
        `/${currentLangInUrl}`,
        `/${i18n.language}`
      );
      navigate(newPath + location.search + location.hash, { replace: true });
    }
  }, [i18n.language, location, navigate]);

  return { currentLanguage: i18n.language };
};

/**
 * Función para obtener el idioma detectado del navegador
 */
export const getDetectedLanguage = (): string => {
  // 1. Verificar localStorage
  const storedLang = localStorage.getItem("i18nextLng");
  if (storedLang && SUPPORTED_LANGUAGES.includes(storedLang)) {
    return storedLang;
  }

  // 2. Verificar URL actual
  const pathLang = window.location.pathname.split("/")[1];
  if (pathLang && SUPPORTED_LANGUAGES.includes(pathLang)) {
    return pathLang;
  }

  // 3. Detectar del navegador
  const browserLang = navigator.language || (navigator as any).userLanguage;
  const langCode = browserLang.split("-")[0].toLowerCase();

  return SUPPORTED_LANGUAGES.includes(langCode) ? langCode : DEFAULT_LANGUAGE;
};
